import { Operation } from '@apollo/client';
import * as Sentry from '@sentry/browser';
import { OperationDefinitionNode, OperationTypeNode } from 'graphql';
import { isNil } from 'lodash';
import { ignoreErrors } from './ignoreErrors';
import { MBSentryBreadcrumb, MBSentryIdentifyPayload } from './types';

const DEBUG_SENTRY = import.meta.env.VITE_DEBUG_SENTRY;

export const MBSentry = {
  init() {
    const dsn = import.meta.env.VITE_SENTRY_DSN?.trim() || undefined;
    const release = import.meta.env.VITE_VERSION;
    const environment = import.meta.env.MODE;
    const enabled = environment !== 'development' && !isNil(dsn);

    const context: Sentry.BrowserOptions = {
      dsn,
      release,
      environment,
      enabled,
      ignoreErrors,
    };

    if (DEBUG_SENTRY) {
      console.warn('[MBSentry] init', context);
    }

    Sentry.init(context);
  },

  identify({ id, name, businessId, businessName }: MBSentryIdentifyPayload) {
    if (DEBUG_SENTRY) {
      console.warn('[MBSentry] identify', { id, name, businessId });
    }
    Sentry.configureScope(scope => {
      scope.setTag('platform', 'web');
      scope.setUser({
        id: String(id),
        username: name,
      });
      scope.setTag('businessId', String(businessId));
      scope.setContext('business', {
        id: String(businessId),
        name: businessName,
      });
    });
  },

  exception({ error, data }: { error: Error; data?: Dict<any> }) {
    if (DEBUG_SENTRY) {
      console.warn('[MBSentry] exception', error, data);
    }
    MBSentry.breadcrumb({ data: data ?? {}, message: error.message, category: 'error' });
    Sentry.captureException(error);
  },

  // We log different types of events (analytics, redux, apollo) in breadcrumbs.
  // This makes it easy to understand what events led up to a crash.
  // https://docs.sentry.io/enriching-error-data/breadcrumbs/?platform=javascript
  breadcrumb({ level = 'info', ...payload }: MBSentryBreadcrumb) {
    if (DEBUG_SENTRY) {
      console.warn('[MBSentry]', payload.category, payload.message, payload.data);
    }
    Sentry.addBreadcrumb({ level, ...payload });
  },

  // TODO(fant): copy paste from index.native.ts
  breadcrumbFromApolloOperation(operation: Operation) {
    // https://github.com/graphql/graphql-js/blob/master/tstypes/language/ast.d.ts#L201
    const definition = operation.query.definitions[0] as OperationDefinitionNode;
    const type: OperationTypeNode = definition.operation;
    const name = operation.operationName;
    MBSentry.breadcrumb({
      message: `${type} ${name}`,
      category: 'apollo',
      data: operation.variables,
    });
  },

  message(text: string) {
    Sentry.captureMessage(text);
  },
};
